import { launchError, launchSuccess } from "components/notifications";
import { useRef, useState } from "react";
import { useTranslation } from 'react-i18next';
import { updateUser } from "services/fetchUserById";
import { uploadUserPicture } from "services/userServices";
import './userEditInfos.css'

const UserEditInfos = ({ userData, setUserData, user, id }) => {
  const { t } = useTranslation();

  const buildEditionData = (data) => ({
    firstName: data.firstName,
    lastName: data.lastName,
    company: data.company,
    email: data.email,
    phone: data.phone,
  })

  const [isEditing, setIsEditing] = useState(false);
  const [editionData, setEditionData] = useState(buildEditionData(userData));
  const [pictureFile, setPictureFile] = useState(null);
  const [picturePreview, setPicturePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const pictureInputRef = useRef(null);

  const handleCancel = () => {
    setEditionData(buildEditionData(userData));
    setPictureFile(null);
    setPicturePreview(null);
    setIsEditing(false);
  }

  const handlePictureChange = (e) => {
    const file = e.target.files[0];
    if (!file || !file.type.startsWith('image/')) return;
    setPictureFile(file);
    setPicturePreview(URL.createObjectURL(file));
  }

  const toBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  })

  const handleConfirm = async () => {
    try {
      setLoading(true);

      await updateUser(user, id, editionData, userData.is_deleted);
      setUserData(prev => ({ ...prev, ...editionData }));

      if (pictureFile) {
        const base64 = await toBase64(pictureFile);
        const result = await uploadUserPicture({ user, id, type: 'profile', picture: base64 });
        setUserData(prev => ({ ...prev, pic: id }));
        setPictureFile(null);
        setPicturePreview(null);
      }

      launchSuccess(t('notifications.users.userById.update.success'));
      setIsEditing(false);
    } catch (error) {
      console.error(error);
      launchError(t('notifications.users.userById.update.error'));
    } finally {
      setLoading(false);
    }
  }

  const currentPicUrl = picturePreview ?? (userData.pic
    ? `${process.env.REACT_APP_CDN_URL}/usr/pic/${userData.pic}`
    : null)

  return (
    <div className='uei_container'>
      <div className='uei_header'>
        <p className='uei_title'>Informations à modifier</p>
        {!isEditing ? (
          <button className='uei_edit_button' onClick={() => setIsEditing(true)}>Modifier</button>
        ) : (
          <div className='uei_action_buttons'>
            <button className='uei_cancel_button' onClick={handleCancel} disabled={loading}>Annuler</button>
            <button className='uei_confirm_button' onClick={handleConfirm} disabled={loading}>
              {loading ? 'Enregistrement...' : 'Valider'}
            </button>
          </div>
        )}
      </div>

      {isEditing && <div className='uei_fields_grid'>
        {/* Profile picture */}
        <div className='uei_field uei_field_picture'>
          <span className='uei_label'>Image de profil</span>
          <div className='uei_picture_row'>
            {currentPicUrl && (
              <img
                src={currentPicUrl}
                alt='profile preview'
                className='uei_picture_preview'
              />
            )}
            {isEditing && (
              <>
                <button
                  type='button'
                  className='uei_picture_button'
                  onClick={() => pictureInputRef.current?.click()}
                >
                  {currentPicUrl ? "Changer l'image" : 'Ajouter une image'}
                </button>
                {pictureFile && (
                  <span className='uei_picture_filename'>{pictureFile.name}</span>
                )}
                <input
                  ref={pictureInputRef}
                  type='file'
                  accept='image/*'
                  style={{ display: 'none' }}
                  onChange={handlePictureChange}
                />
              </>
            )}
          </div>
        </div>

        {/* Text fields */}
        {[
          { label: 'Prénom', key: 'firstName', type: 'text' },
          { label: 'Nom', key: 'lastName', type: 'text' },
          { label: 'Société', key: 'company', type: 'text' },
          { label: 'Adresse email', key: 'email', type: 'email' },
          { label: 'Numéro de téléphone', key: 'phone', type: 'text' },
        ].map(({ label, key, type }) => (
          <div key={key} className='uei_field'>
            <span className='uei_label'>{label}</span>
            {isEditing ? (
              <input
                type={type}
                value={editionData[key] ?? ''}
                className='uei_input'
                onChange={(e) => setEditionData(prev => ({ ...prev, [key]: e.target.value }))}
              />
            ) : (
              <span className='uei_value'>{userData[key] ?? '—'}</span>
            )}
          </div>
        ))}
      </div>}
    </div>
  )
}

export default UserEditInfos
